import React, { useState, useEffect } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import Typography from 'src/components/Text/Text';
import { themes } from 'src/theme/theme';
import { RootState } from 'src/store/store';
import {
  useUpdateCompanyNotesMutation,
  useGetCompanyNotesQuery,
} from 'src/features/company/api/companyApi';

const SidebarNotesWidget: React.FC = () => {
  const [notes, setNotes] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  const currentCompanyId = useSelector((state: RootState) => state.company.company?.id);
  const companyName = useSelector((state: RootState) => state.company.company?.companyName);
  const producerFormId = useSelector((state: RootState) => state.users.producerFormId);

  const [updateCompanyNotes] = useUpdateCompanyNotesMutation();
  const { data: savedNotes, isLoading: isFetchingNotes } = useGetCompanyNotesQuery(
    currentCompanyId?.toString() || '',
    { skip: !currentCompanyId },
  );

  useEffect(() => {
    if (savedNotes !== undefined) {
      setNotes(savedNotes || '');
    }
  }, [savedNotes]);

  const handleSave = async () => {
    if (!currentCompanyId) return;

    try {
      setSaveStatus('saving');
      await updateCompanyNotes({
        companyId: currentCompanyId.toString(),
        note: notes,
        producerFormId: producerFormId || '',
      }).unwrap();

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save notes:', error);
      setSaveStatus('idle');
    }
  };

  if (!currentCompanyId) return null;

  const isDirty = notes !== (savedNotes || '');

  return (
    <View style={styles.container}>
      <Typography variant="caption1Medium" style={styles.header}>
        NOTES
      </Typography>
      {companyName && (
        <Typography variant="caption2" style={styles.subtitle} numberOfLines={1}>
          {companyName}
        </Typography>
      )}

      {isFetchingNotes ? (
        <Typography variant="caption2" style={styles.loadingText}>
          Loading notes...
        </Typography>
      ) : (
        <TextInput
          style={styles.textInput}
          placeholder="Add notes..."
          placeholderTextColor={themes.light.gray[5]}
          multiline
          value={notes}
          onChangeText={setNotes}
          textAlignVertical="top"
        />
      )}

      <TouchableOpacity
        style={[styles.saveButton, !isDirty && styles.saveButtonDisabled]}
        onPress={handleSave}
        disabled={!isDirty || saveStatus === 'saving'}>
        <Typography variant="caption1Medium" style={styles.saveButtonText}>
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Save'}
        </Typography>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: themes.light.gray[7],
  },
  header: {
    color: themes.light.gray[5],
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  subtitle: {
    color: themes.light.gray[5],
    marginBottom: 8,
  },
  loadingText: {
    color: themes.light.gray[5],
    paddingVertical: 16,
  },
  textInput: {
    borderWidth: 1,
    borderColor: themes.light.gray_blue[6],
    borderRadius: 6,
    padding: 8,
    minHeight: 80,
    maxHeight: 120,
    fontSize: 13,
    color: themes.light.gray_blue[1],
    backgroundColor: themes.light.white[1],
  },
  saveButton: {
    backgroundColor: themes.light.brand_blue[3],
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: themes.light.white[1],
  },
});

export default SidebarNotesWidget;
